import React, { useState } from 'react'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Switch } from '../../components/ui/Switch'
import { useAuth } from '../../hooks/useAuth'

// Mock data for settings
const mockSettings = {
  profile: {
    name: 'Alex Johnson',
    email: 'alex@techstartup.com',
    role: 'Editor',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    timezone: 'America/New_York',
    language: 'English'
  },
  notifications: {
    email: true,
    push: false,
    sms: false,
    weeklyDigest: true,
    contentReminders: true,
    engagementAlerts: true
  },
  integrations: [
    {
      id: 1,
      name: 'LinkedIn',
      status: 'connected',
      account: 'TechStartup Inc',
      lastSync: '2 hours ago',
      icon: '🔗'
    },
    {
      id: 2,
      name: 'Twitter',
      status: 'connected',
      account: '@techstartup',
      lastSync: '1 hour ago',
      icon: '🐦'
    },
    {
      id: 3,
      name: 'Facebook',
      status: 'disconnected',
      account: null,
      lastSync: null,
      icon: '📘'
    },
    {
      id: 4,
      name: 'Instagram',
      status: 'pending',
      account: 'techstartup_official',
      lastSync: '3 days ago',
      icon: '📷'
    }
  ],
  billing: {
    plan: 'Pro',
    status: 'active',
    nextBilling: '2024-09-01',
    credits: 150,
    usage: {
      posts: 45,
      aiGenerations: 23,
      storage: '2.3 GB'
    }
  }
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('profile')
  const [notifications, setNotifications] = useState(mockSettings.notifications)
  const [profile, setProfile] = useState(mockSettings.profile)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'disconnected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
    }
  }

  const tabs = [
    { id: 'profile', label: 'Profile', icon: '👤' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'integrations', label: 'Integrations', icon: '🔗' },
    { id: 'billing', label: 'Billing', icon: '💳' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'preferences', label: 'Preferences', icon: '⚙️' }
  ]

  return (
    <div className="space-y-8 fade-in">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold gradient-text">
          Settings
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage your account preferences and integrations
        </p>
      </div>

      {/* Settings Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <Card className="card-modern slide-up" style={{ animationDelay: '0.1s' }}>
            <div className="p-4">
              <nav className="space-y-2">
                {tabs.map((tab, index) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <span className="text-lg">{tab.icon}</span>
                    <span className="font-medium">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeTab === 'profile' && (
            <Card className="card-modern slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Profile Settings</h2>
                
                <div className="space-y-6">
                  {/* Avatar */}
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-muted">
                      <img 
                        src={profile.avatar} 
                        alt={profile.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <Button variant="outline" size="sm" className="btn-outline">Change Avatar</Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        JPG, PNG or GIF. Max size 2MB.
                      </p>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Full Name</label>
                      <Input
                        value={profile.name}
                        onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                        placeholder="Enter your full name"
                        className="input-modern"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <Input
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        placeholder="Enter your email"
                        type="email"
                        className="input-modern"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Role</label>
                      <Select
                        value={profile.role}
                        onValueChange={(value) => setProfile({ ...profile, role: value })}
                      >
                        <option value="Editor">Editor</option>
                        <option value="Manager">Manager</option>
                        <option value="Admin">Admin</option>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Timezone</label>
                      <Select
                        value={profile.timezone}
                        onValueChange={(value) => setProfile({ ...profile, timezone: value })}
                      >
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">Pacific Time</option>
                      </Select>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button className="btn-primary">Save Changes</Button>
                    <Button variant="outline" className="btn-outline">Cancel</Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card className="card-modern slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Notification Preferences</h2>
                
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Email Notifications</h3>
                        <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                      </div>
                      <Switch
                        checked={notifications.email}
                        onCheckedChange={(checked) => setNotifications({ ...notifications, email: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Push Notifications</h3>
                        <p className="text-sm text-muted-foreground">Receive push notifications in browser</p>
                      </div>
                      <Switch
                        checked={notifications.push}
                        onCheckedChange={(checked) => setNotifications({ ...notifications, push: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">SMS Notifications</h3>
                        <p className="text-sm text-muted-foreground">Receive notifications via SMS</p>
                      </div>
                      <Switch
                        checked={notifications.sms}
                        onCheckedChange={(checked) => setNotifications({ ...notifications, sms: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Weekly Digest</h3>
                        <p className="text-sm text-muted-foreground">Get a weekly summary of your content performance</p>
                      </div>
                      <Switch
                        checked={notifications.weeklyDigest}
                        onCheckedChange={(checked) => setNotifications({ ...notifications, weeklyDigest: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Content Reminders</h3>
                        <p className="text-sm text-muted-foreground">Get reminded about scheduled content</p>
                      </div>
                      <Switch
                        checked={notifications.contentReminders}
                        onCheckedChange={(checked) => setNotifications({ ...notifications, contentReminders: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">Engagement Alerts</h3>
                        <p className="text-sm text-muted-foreground">Get notified about high engagement on your posts</p>
                      </div>
                      <Switch
                        checked={notifications.engagementAlerts}
                        onCheckedChange={(checked) => setNotifications({ ...notifications, engagementAlerts: checked })}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button className="btn-primary">Save Preferences</Button>
                    <Button variant="outline" className="btn-outline">Reset to Default</Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'integrations' && (
            <Card className="card-modern slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Social Media Integrations</h2>
                
                <div className="space-y-4">
                  {mockSettings.integrations.map((integration, index) => (
                    <div key={integration.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="text-2xl">{integration.icon}</div>
                        <div>
                          <h3 className="font-medium">{integration.name}</h3>
                          {integration.account && (
                            <p className="text-sm text-muted-foreground">{integration.account}</p>
                          )}
                          {integration.lastSync && (
                            <p className="text-xs text-muted-foreground">Last sync: {integration.lastSync}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Badge className={`text-xs ${getStatusColor(integration.status)}`}>
                          {integration.status}
                        </Badge>
                        <Button variant="outline" size="sm" className="btn-outline">
                          {integration.status === 'connected' ? 'Manage' : 'Connect'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                    💡 Pro Tip
                  </h3>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Connect your social media accounts to automatically publish content and track engagement across all platforms.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'billing' && (
            <Card className="card-modern slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Billing & Usage</h2>
                
                <div className="space-y-6">
                  {/* Current Plan */}
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold">{mockSettings.billing.plan} Plan</h3>
                        <p className="text-sm text-muted-foreground">
                          Next billing: {new Date(mockSettings.billing.nextBilling).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={getStatusColor(mockSettings.billing.status)}>
                        {mockSettings.billing.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-primary">{mockSettings.billing.credits}</p>
                        <p className="text-xs text-muted-foreground">Credits Remaining</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{mockSettings.billing.usage.posts}</p>
                        <p className="text-xs text-muted-foreground">Posts This Month</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{mockSettings.billing.usage.aiGenerations}</p>
                        <p className="text-xs text-muted-foreground">AI Generations</p>
                      </div>
                    </div>
                  </div>

                  {/* Usage Details */}
                  <div>
                    <h3 className="font-semibold mb-4">Usage Details</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Storage Used</span>
                        <span className="text-sm font-medium">{mockSettings.billing.usage.storage}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Posts Published</span>
                        <span className="text-sm font-medium">{mockSettings.billing.usage.posts}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm">AI Content Generated</span>
                        <span className="text-sm font-medium">{mockSettings.billing.usage.aiGenerations}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button className="btn-primary">Upgrade Plan</Button>
                    <Button variant="outline" className="btn-outline">View Invoice</Button>
                    <Button variant="outline" className="btn-outline">Download Usage Report</Button>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card className="card-modern slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Security Settings</h2>
                
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">Change Password</h3>
                      <div className="space-y-3">
                        <Input type="password" placeholder="Current password" className="input-modern" />
                        <Input type="password" placeholder="New password" className="input-modern" />
                        <Input type="password" placeholder="Confirm new password" className="input-modern" />
                      </div>
                      <Button className="btn-primary mt-3">Update Password</Button>
                    </div>
                    
                    <div className="border-t border-border pt-6">
                      <h3 className="font-medium mb-2">Two-Factor Authentication</h3>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Add an extra layer of security to your account
                        </p>
                        <Button variant="outline" size="sm" className="btn-outline">Enable 2FA</Button>
                      </div>
                    </div>
                    
                    <div className="border-t border-border pt-6">
                      <h3 className="font-medium mb-2">Active Sessions</h3>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div>
                            <p className="text-sm font-medium">Current Session</p>
                            <p className="text-xs text-muted-foreground">Chrome on Mac • New York, NY</p>
                          </div>
                          <Badge variant="secondary" className="text-xs">Active</Badge>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="btn-outline mt-3">Sign Out All Sessions</Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'preferences' && (
            <Card className="card-modern slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Preferences</h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium mb-4">Content Preferences</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Default Content Length</label>
                        <Select defaultValue="medium">
                          <option value="short">Short (100-200 words)</option>
                          <option value="medium">Medium (200-500 words)</option>
                          <option value="long">Long (500+ words)</option>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Default Tone</label>
                        <Select defaultValue="professional">
                          <option value="professional">Professional</option>
                          <option value="casual">Casual</option>
                          <option value="friendly">Friendly</option>
                          <option value="authoritative">Authoritative</option>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-2">Auto-save Interval</label>
                        <Select defaultValue="5">
                          <option value="1">1 minute</option>
                          <option value="5">5 minutes</option>
                          <option value="10">10 minutes</option>
                          <option value="30">30 minutes</option>
                        </Select>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-border pt-6">
                    <h3 className="font-medium mb-4">Display Preferences</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Compact Mode</p>
                          <p className="text-sm text-muted-foreground">Show more content in less space</p>
                        </div>
                        <Switch />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Show Analytics</p>
                          <p className="text-sm text-muted-foreground">Display engagement metrics</p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button className="btn-primary">Save Preferences</Button>
                    <Button variant="outline" className="btn-outline">Reset to Default</Button>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default SettingsPage